// The deck host (P2.1) — the main-thread side of deck-processor.ts. It decodes files, builds track
// memory, owns the node and the command ring, and gets out of the way.
// Nothing here runs per block or on a clock (Architecture §2).
//
// **It stopped creating the context at P3.7.** With one deck, "the deck's context" and "the app's
// context" were the same object and nothing could tell them apart; with two, a context per deck is
// two clocks Sync could never align. `audio-graph.ts` owns the context, the worklet module and the
// signal ring now, and this owns everything there is one of *per deck*.
//
// **This is the engine now.** It was built alongside `AudioEngine`, the M0 walking skeleton's host,
// rather than as a mode of it — which meant the demo could not regress while the deck was built,
// and neither class had to grow two unrelated personalities. They shared the worklet bundle and the
// primitives (the command ring shape, `ClockDriftMonitor`) and never state, and that is exactly why
// P2.12's removal was a deletion rather than an untangling.
//
// Two things followed it here rather than retiring with it, both because they were infrastructure
// rather than scaffolding: Architecture §4.5's latency probe, and the synthetic-load knob that
// makes NFR-P2's underrun counter falsifiable.

import { ClockDriftMonitor, type UnderrunReport } from './clock-drift';
import type { EngineCommand } from './control-mapping';
import {
  clearHistogram,
  readDistribution,
  takeWindow,
  type DistributionMs,
} from '../../platform/sab/histogram';
import { createRing } from '../../platform/sab/ring';
import { SignalReader } from '../../platform/sab/signal-bus';
import { NO_SLOT, RejectRingWriter } from '../../platform/sab/spsc-ring';
import { createTrackMemory } from '../../platform/sab/track-memory';
import type { DeckId } from '../../platform/types/control';
import {
  COMMAND_RECORD,
  COMMAND_RING,
  SIGNAL_SOURCE,
  type SignalSourceIndex,
} from '../../platform/types/rings';
import {
  computeTrackMemoryLayout,
  TRACK_MEMORY_FORMAT,
  type TrackMemoryFormat,
  type TrackMemoryLayout,
  type TrackMemorySpec,
} from '../../platform/types/track-memory';
import {
  COMMAND_PROBE,
  COMMAND_PROBE_BYTES,
  LATENCY_HISTOGRAM,
} from '../../platform/types/probe';
import type { LoopQuantization } from '../../platform/types/loop';
import type { SyncGrid } from '../../platform/types/sync';
import { stripIndexForDeck } from '../../platform/types/mixer';
import {
  DECK_DEFAULT_RATE,
  DECK_DEFAULT_TRACK_GAIN,
  DECK_MAX_RATE,
  DECK_OPCODE,
  DECK_PROCESSOR,
  DECK_STATUS,
  DECK_STATUS_BYTES,
  type DeckBeatGridMessage,
  type DeckHotCueMessage,
  type DeckLoopMessage,
  type DeckMessage,
  type DeckRestoreHotCuesMessage,
  type DeckProcessorOptions,
  type DeckSyncPhaseMessage,
  type DeckTrackGainMessage,
} from '../../platform/types/deck';
import {
  phaseFromCode,
  type TransportEvent,
  type TransportPhase,
} from '../../platform/types/transport';

/**
 * Where the worklet bundle lives. The same URL in dev and in production, deliberately: in
 * production it is the file vite.bundles.ts emits, and in dev a middleware bundles the same entry
 * with esbuild and serves it here (see vite.config.ts).
 *
 * Same bytes, same shape, same guard — because the alternative is dev relying on browser support
 * for static imports inside a worklet, which is uneven outside Chromium, and Tier 2 must degrade
 * rather than crash (PRD §6.3).
 *
 * **Moved here from `audio-engine.ts` at P2.12**, when that file retired. The deck is the only
 * caller now, and a constant with one consumer belongs beside it.
 */
export const WORKLET_MODULE_URL = '/audio-worklet.js';

/** What the device costs before a sample reaches anyone's ears (P2.12, from `AudioEngineInfo`). */
export interface DeckLatencyInfo {
  readonly sampleRateHz: number;
  /** The context's own buffering, in seconds. Every browser reports this. */
  readonly baseLatencySeconds: number;
  /**
   * Time from the audio thread producing a sample to it leaving the speaker, in seconds.
   * `null` where the browser does not implement it (Safari, as of writing).
   */
  readonly outputLatencySeconds: number | null;
  /** The two above, summed — the number NFR-P1's 35 ms budget is actually spent against. */
  readonly totalLatencySeconds: number | null;
}

/** Architecture §4.5's probe, read back. Every figure is command→audible, not internal. */
export interface LatencyProbe {
  /** The most recent command's journey from written to audible, in ms. */
  readonly lastMs: number | null;
  /** The worst since reset. A mean hides the glitch. */
  readonly maxMs: number | null;
  readonly appliedCount: number;
  /** Commands the ring refused because it was full (P0.3's reject-and-count). */
  readonly rejectedCount: number;
}

/**
 * `outputLatency` typed honestly.
 *
 * lib.dom declares it `number`, non-optional. Safari does not implement it and returns undefined,
 * so the type is a lie on ~15% of desktop. Reading it through this shape is what makes the
 * `undefined` check legal rather than something the linter deletes as unreachable.
 */
interface MaybeOutputLatency {
  readonly outputLatency?: number;
}

const PROBE_LAST_I32 = COMMAND_PROBE.LAST_APPLY_MICROS >>> 2;
const PROBE_MAX_I32 = COMMAND_PROBE.MAX_APPLY_MICROS >>> 2;
const PROBE_COUNT_I32 = COMMAND_PROBE.APPLIED_COUNT >>> 2;
const PROBE_HISTOGRAM_I32 = COMMAND_PROBE.APPLY_HISTOGRAM >>> 2;

const POSITION_F64_INDEX = DECK_STATUS.POSITION_FRAMES >>> 3;
const SPIN_I32_INDEX = DECK_STATUS.SYNTHETIC_SPINS >>> 2;
const SYNC_ERROR_F64_INDEX = DECK_STATUS.SYNC_PHASE_ERROR_SECONDS >>> 3;
const LOOP_START_F64_INDEX = DECK_STATUS.LOOP_START_FRAMES >>> 3;
const LOOP_END_F64_INDEX = DECK_STATUS.LOOP_END_FRAMES >>> 3;
const LOOP_ACTIVE_I32_INDEX = DECK_STATUS.LOOP_ACTIVE >>> 2;
const HOT_CUES_F64_INDEX = DECK_STATUS.HOT_CUES >>> 3;

/**
 * How decoded audio is stored — **SP-4's ruling, applied** (`ADR-LOG.md`, SP-4 section).
 *
 * Float32, because the measurement found CPU is not a differentiator (both formats land at ~1% of
 * the block budget for two decks) which left the choice as memory against quality, and nothing yet
 * forces that trade: Float32 costs 29.3% of NFR-P5's ceiling and leaves ~850 MiB spare, while
 * Int16's cost — 84.1 dB SNR, and intersample peaks above 0 dBFS hard-clamped — is unrecoverable.
 *
 * **Int16 is a lever, not a dead branch.** `platform/sab/track-memory.ts` still implements it in
 * full, `tests/perf/sp4-format-bench.ts` still exercises and prices it, and pulling it means
 * changing this constant and adding a setting. Pull it if M7's visuals or M9's library make memory
 * binding; the ADR has the numbers so the decision does not need re-running.
 */
const TRACK_STORAGE_FORMAT: TrackMemoryFormat = TRACK_MEMORY_FORMAT.F32;

/** Always store stereo, per track-memory.ts's header comment. A mono file is up-mixed here — one
 * decoded channel copied into both — rather than the reader carrying a "maybe mono" branch on its
 * hot path for a case that is rare in DJ libraries and free to handle once, off the audio thread. */
function toStereoChannels(buffer: AudioBuffer): [Float32Array, Float32Array] {
  const left = buffer.getChannelData(0);
  if (buffer.numberOfChannels === 1) {
    return [left, left.slice()];
  }
  return [left, buffer.getChannelData(1)];
}

/**
 * What a deck needs from the graph it lives on (P3.7).
 *
 * A structural pair rather than `AudioGraph` itself, for two reasons. `/audio/engine` gets no import
 * cycle — `audio-graph.ts` imports `Deck` to build one, and a `Deck` that imported `AudioGraph`
 * back would fail `no-circular`. And a test can hand a deck a context and a ring without standing up
 * a graph, which is the only reason any of this is reachable from Node at all.
 */
export interface DeckGraph {
  readonly context: AudioContext;
  /** The one FR-26 signal ring. Both decks write into it — see `SignalPublisher.openQuantum`. */
  readonly signals: SharedArrayBuffer;
  /** Which ring that is, as a number the audio thread can key on. See `DeckProcessorOptions`. */
  readonly signalsId: number;
  /**
   * FR-14's mixer, and since P4.2 the only thing a deck connects to (P4.2).
   *
   * A deck used to reach `context.destination` directly, at full level, which `audio-graph.ts` had
   * flagged since P3.7 as the shape that would change the day the mixer arrived. It has. Which
   * *input* of the mixer this deck takes is `stripIndexForDeck`'s answer, not the graph's — §2's
   * ruling is that adding M6's third strip must be a call site, and the first thing a
   * re-derivation gets wrong is which input something was connected to.
   */
  readonly mixer: AudioNode;
}

export interface TrackInfo {
  readonly frameCount: number;
  readonly channelCount: number;
  readonly sampleRateHz: number;
  readonly durationSeconds: number;
}

export class Deck {
  /** Which deck this is (FR-01). Both exist since P3.7; it decides the signal-source block here and
   * in the processor, and it is the namespace every control id for this deck is under. */
  readonly id: DeckId;
  readonly #context: AudioContext;
  readonly #mixer: AudioNode;
  #node: AudioWorkletNode | null = null;
  #commands: RejectRingWriter | null = null;
  #timestamps: Float64Array | null = null;
  #shorts: Uint16Array | null = null;
  #floats: Float32Array | null = null;
  #status: Float64Array | null = null;
  #layout: TrackMemoryLayout | null = null;

  // Kept from P2.5 so the analysis worker can read the PCM *in place*. The alternative was handing
  // it the decoded `Float32Array`s, which would have meant either a ~138 MB structured-clone copy
  // on the load path or transferring them and detaching the arrays `#rebuild` needs. Shared memory
  // is already shared with every thread; there was never a reason to move the samples twice.
  #trackMemory: SharedArrayBuffer | null = null;

  // The buffer the *audio thread* writes the playhead into. Handed to the waveform worker at P2.6
  // so the playhead it draws comes from the transport's owner rather than from a rAF clock —
  // Architecture §7: the UI renders transport state, it never computes it.
  #statusBuffer: SharedArrayBuffer | null = null;

  // Kept after decode so track memory can be rebuilt without decoding the file again. P1.1 needed
  // that for its format A/B; it stays because rebuilding is also how the node is replaced, and
  // re-running decodeAudioData on an 8-minute file to swap a node would be absurd.
  #decodedChannels: readonly [Float32Array, Float32Array] | null = null;

  #statusInts: Int32Array | null = null;

  /**
   * The signal ring (P2.9), created once per deck rather than per track — and **since P3.7 owned by
   * the graph rather than by the deck**, because both decks write into one ring.
   *
   * Deliberately *not* rebuilt in `#rebuild` alongside the node: a ring rebuilt mid-session would
   * reset the sequence the reader uses to spot dropped frames, and the reader would read the reset
   * as a catastrophic drop. The processor is handed the same buffer every time — and now so is the
   * other deck, which is why moving it out was not merely tidier.
   */
  readonly #signals: SharedArrayBuffer;
  /** The ring's id, carried into every processor this deck builds. See `DeckProcessorOptions`. */
  readonly #signalsId: number;

  /**
   * This deck's own reader over the shared ring.
   *
   * **Two readers on one overwrite ring, and that is sound rather than tolerated.** Each keeps its
   * own read position in a field; the writer never consults a reader's position (P0.3 — that is the
   * whole point of overwrite-oldest), so neither can starve or mislead the other. What they do share
   * is the header's bookkeeping: `READ_SEQ` is written by whichever read last and only ever read by
   * a *newly constructed* reader, and `dropped` becomes the sum across both. Worth knowing before
   * reading `signalsDropped` off one deck and calling it that deck's figure.
   */
  readonly #signalReader: SignalReader;

  /** Which block of the shared frame this deck writes and reads — the same choice `deckId` makes on
   * the audio thread (`deck-processor.ts`), made once more here so the meter reads what the
   * processor wrote rather than what deck A wrote. */
  readonly #source: SignalSourceIndex;

  /**
   * Architecture §4.5's latency probe (P2.12, inherited from `AudioEngine`).
   *
   * Built once for the life of the deck and **deliberately not rebuilt in `#rebuild`**, unlike the
   * command ring and the status buffer. Those are per-node because a new processor needs a clean
   * ring; the probe is a *measurement of this deck over time*, and resetting it on every track load
   * would make a session-long latency distribution impossible to collect — which is exactly what
   * P2.10's telemetry wants. `resetProbe()` is the explicit way to clear it, and the bench is the
   * only caller.
   */
  readonly #probe: SharedArrayBuffer = new SharedArrayBuffer(COMMAND_PROBE_BYTES);
  readonly #probeInts: Int32Array;

  /**
   * The rate last asked for, so a node rebuilt mid-session comes back at the same speed.
   *
   * The processor is born at `DECK_DEFAULT_RATE`, which is what stops a fresh deck being silent.
   * This field is the other half: once a pitch fader exists (M3) and the user has set 1.04, a
   * `#rebuild` would otherwise hand them a processor quietly back at 1.00 while the fader still
   * reads 1.04. Re-arming costs one ring write per load.
   */
  #rate = DECK_DEFAULT_RATE;

  /**
   * FR-10's gain for the track currently loaded (P3.2).
   *
   * **Reset to unity by every new track, not carried across one.** Loudness is a property of the
   * file, so keeping the previous track's gain would play the new one at a level measured from
   * something else — for the second or two before its own loudness stage settles, which is exactly
   * the window in which a DJ is listening. The same reasoning `#rebuild` uses for refusing to
   * inherit a cue point.
   */
  #trackGain = DECK_DEFAULT_TRACK_GAIN;

  /**
   * The beatgrid last handed to the audio thread (P3.9).
   *
   * Kept for `#rebuild`'s re-arm, like `#rate` and `#trackGain` — and **not** reset by a new track,
   * unlike the gain: the loader sets it when analysis lands, and clearing it here would race that.
   * A track with no detected tempo gets an explicit `null`.
   */
  #beatGrid: SyncGrid | null = null;

  /**
   * The cue points last handed to the audio thread (P3.10), for `#rebuild`'s re-arm.
   *
   * **Not cleared by a new track**, unlike the track gain: the loader restores them from the cache
   * when a track's record arrives, and clearing here would race that. A track with no stored cues
   * gets an explicit array of `NaN`.
   */
  #hotCues: readonly number[] = [];

  readonly #drift: ClockDriftMonitor;
  /** Has the audio clock been seen to advance since the context last started? See `sampleClockDrift`. */
  #clockStarted = false;
  /** The previous `currentTime` reading, used only by the startup gate above. */
  #lastAudioMs: number | null = null;

  private constructor(graph: DeckGraph, id: DeckId) {
    this.id = id;
    this.#context = graph.context;
    this.#mixer = graph.mixer;
    this.#signals = graph.signals;
    this.#signalsId = graph.signalsId;
    this.#source = id === 'b' ? SIGNAL_SOURCE.DECK_B : SIGNAL_SOURCE.DECK_A;
    this.#drift = new ClockDriftMonitor(graph.context.sampleRate);
    this.#signalReader = new SignalReader(this.#signals);
    this.#probeInts = new Int32Array(this.#probe);
  }

  /**
   * A deck on an already-open graph (P3.7). Synchronous, and that is the whole shape of the change:
   * everything that had to be awaited — the context, the `addModule` — belongs to the graph and
   * happened once.
   *
   * `AudioGraph.createDeck` is the ordinary way in. This takes the structural pair rather than the
   * class so `/audio/engine` has no cycle in it and a test can hand a deck a fake graph.
   */
  static on(graph: DeckGraph, id: DeckId): Deck {
    return new Deck(graph, id);
  }

  /**
   * Decodes encoded audio into track memory and (re)builds the node — the same pattern App.tsx uses
   * for P0.8's render-path toggle. A deck is not the SAB rings racing each other: no concurrent
   * access spans the swap, so a clean stop-then-start is correct and simple rather than a
   * compromise.
   *
   * **Took a `File` until P3.8.** FR-12's cache needs the encoded bytes hashed *before* they are
   * decoded, because `decodeAudioData` detaches the buffer — and reading an 8 MB file twice to keep
   * a tidier signature would cost more than the hash itself does (8.5 ms, measured).
   */
  async loadEncoded(bytes: ArrayBuffer): Promise<TrackInfo> {
    // decodeAudioData resamples to the calling context's rate — this is what makes `rate` in
    // deck-processor.ts a pure speed multiplier with no sample-rate-conversion math anywhere: the
    // track's stored rate and the context's rate are the same rate by construction.
    //
    // ⚠️ **It also detaches `bytes`.** Anything the caller wants from the encoded file — P3.8 wants
    // its SHA-256 and its length — has to be taken before this line, which is why this takes a
    // buffer rather than a `File`: the caller reads the file once and uses it twice.
    const audioBuffer = await this.#context.decodeAudioData(bytes);

    this.#decodedChannels = toStereoChannels(audioBuffer);
    // A new file has not been measured yet, so it plays as recorded until its loudness stage
    // settles. See `#trackGain` for why inheriting the last track's gain would be worse.
    this.#trackGain = DECK_DEFAULT_TRACK_GAIN;
    return Promise.resolve(this.#rebuild());
  }

  /**
   * Load audio that was generated rather than decoded (P2.12).
   *
   * `/bench` is the caller and the reason this exists. Until P2.12 the bench drove the tone
   * generator, which needed nothing but a click; the deck needs a *track*, and a benchmark that
   * demanded the operator find an audio file first would be a benchmark nobody runs — worse, one
   * whose numbers depend on which file they picked. A generated track makes the run reproducible
   * and removes any question of committing music to the repo (PRD OQ-2's licensing is unresolved,
   * and P2.13 makes the same call for its fixtures).
   *
   * Samples must already be at `sampleRateHz`; there is no resampling here on purpose, because
   * `decodeAudioData` is what guarantees that invariant on the file path and a second, weaker
   * guarantee would be worse than none.
   */
  loadChannels(channels: readonly Float32Array[]): TrackInfo {
    const [left, right] = channels;
    if (left === undefined) {
      throw new RangeError('loadChannels needs at least one channel.');
    }

    // Mono in, stereo out — the same widening `toStereoChannels` applies to a decoded mono file,
    // so both load paths hand `#rebuild` the identical shape and it never learns which was used.
    this.#decodedChannels = [left, right ?? left];
    this.#trackGain = DECK_DEFAULT_TRACK_GAIN;
    return this.#rebuild();
  }

  /** Synchronous, deliberately not `async` — nothing below awaits anything (SharedArrayBuffer
   * allocation and node construction are both immediate). `loadFile` wraps the result in a
   * resolved Promise rather than this pretending to do asynchronous work it does not do. */
  #rebuild(): TrackInfo {
    const channels = this.#decodedChannels;
    if (channels === null) {
      throw new Error('No track loaded.');
    }

    // Only ever reached from `loadFile`, so in practice this is always a *new* track and there is
    // nothing to preserve. Kept because the mechanism costs nothing and P1.1 needed it for a
    // mid-listen node swap; the transport deliberately is **not** preserved — a new track has no
    // cue point, and inheriting the previous track's would put it at an arbitrary place in this one.
    const wasRunning = this.phase !== 'paused';
    const previousPosition = this.readPlayheadFrames();

    const layout = computeTrackMemoryLayout({
      channelCount: 2,
      frameCount: channels[0].length,
      format: TRACK_STORAGE_FORMAT,
    });
    const trackMemory = createTrackMemory(layout, channels, this.#context.sampleRate);

    this.#node?.port.close();
    this.#node?.disconnect();

    const commandRing = createRing(COMMAND_RING);
    const status = new SharedArrayBuffer(DECK_STATUS_BYTES);

    const processorOptions: DeckProcessorOptions = {
      deckId: this.id,
      commandRing,
      trackMemory,
      status,
      signals: this.#signals,
      signalsId: this.#signalsId,
      probe: this.#probe,
    };
    const node = new AudioWorkletNode(this.#context, DECK_PROCESSOR, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions,
    });
    // Into the mixer's strip for this deck, not into `destination` (P4.2). Explicit output 0 and
    // an explicit input index: the two-argument `connect` would silently take input 0, so both decks
    // would land on strip A and deck B would be inaudible as a *level* problem rather than a routing
    // one — audible, plausible, and the wrong thing to debug.
    node.connect(this.#mixer, 0, stripIndexForDeck(this.id));

    this.#node = node;
    this.#commands = new RejectRingWriter(commandRing, COMMAND_RING);
    this.#timestamps = new Float64Array(commandRing);
    this.#shorts = new Uint16Array(commandRing);
    this.#floats = new Float32Array(commandRing);
    this.#status = new Float64Array(status);
    this.#statusInts = new Int32Array(status);
    this.#layout = layout;
    this.#trackMemory = trackMemory;
    this.#statusBuffer = status;

    // A rebuild must not restart the track or interrupt playback: position and transport state
    // survive the node swap.
    // Re-arm the rate before anything else: a new processor starts at the default, and if a pitch
    // has been set this is what carries it across the swap.
    if (this.#rate !== DECK_DEFAULT_RATE) {
      this.setRate(this.#rate);
    }
    // Same re-arming, same reason: a new processor is born at unity, and a normalised track that
    // survives a node swap must not come back at its raw level.
    if (this.#trackGain !== DECK_DEFAULT_TRACK_GAIN) {
      this.setTrackGain(this.#trackGain);
    }
    // Same re-arming, same reason (P3.9): a new processor knows no grid, and a deck that lost its
    // grid to a node swap would be a deck sync silently declines to move.
    if (this.#beatGrid !== null) {
      this.setBeatGrid(this.#beatGrid);
    }
    // Same re-arming, and the one where losing the value would lose *user data* rather than a
    // setting the UI could re-derive (P3.10).
    if (this.#hotCues.length > 0) {
      this.restoreHotCues(this.#hotCues);
    }
    if (previousPosition > 0) {
      this.seek(previousPosition);
    }
    if (wasRunning) {
      this.send('play-pressed');
    }

    return {
      frameCount: layout.frameCount,
      channelCount: layout.channelCount,
      sampleRateHz: this.#context.sampleRate,
      durationSeconds: layout.frameCount / this.#context.sampleRate,
    };
  }

  /**
   * Reports what the human did (P2.7). The machine on the audio thread decides what it means.
   *
   * No `setPlaying(boolean)` any more, and platform/types/deck.ts explains why at length: with cue
   * in the picture, "cue was released" means different things depending on whether the deck is
   * previewing or was latched into play, and only the side holding the state machine can tell.
   */
  send(event: TransportEvent): void {
    const message: DeckMessage = { type: 'transport', event };
    this.#node?.port.postMessage(message);
  }

  /**
   * The phase, read from the buffer the audio thread publishes it into.
   *
   * Read, never cached. A `previewing` deck returns to `paused` on cue release without the main
   * thread being told, and `ended` fires with no user action at all — so a local mirror of this
   * would be wrong within seconds of the first cue press.
   */
  get phase(): TransportPhase {
    const ints = this.#statusInts;
    return ints === null
      ? 'paused'
      : phaseFromCode(Atomics.load(ints, DECK_STATUS.PHASE >>> 2));
  }

  get cuePointFrames(): number {
    return this.#status?.[DECK_STATUS.CUE_POINT_FRAMES >>> 3] ?? 0;
  }

  seek(frame: number): void {
    const message: DeckMessage = { type: 'seek', frame };
    this.#node?.port.postMessage(message);
  }

  /**
   * Writes one engine command to the ring (P3.6).
   *
   * **Architecture §3's audio plane, terminated.** A `ControlEvent` becomes an `EngineCommand` in
   * `control-mapping.ts`, and this is where that command stops being a description and becomes four
   * numbers in shared memory. Nothing above this line knows the ring exists; nothing below it has
   * ever seen a string.
   *
   * The timestamp is `AudioContext.currentTime` at the moment of writing, which is the near end of
   * Architecture §4.5's latency probe — the processor subtracts it from its own `currentTime` when
   * the command is applied. That is why this stamps rather than the caller: a caller that forgot
   * would leave the probe silently reading zero, and zero is a plausible latency.
   *
   * Returns false when the ring is full. P0.3's reject-and-count, not a queue: a fader under a
   * finger can outrun a 2.7 ms block, and dropping the *older* value would be wrong — the newest
   * position is the one the user means, and the next event is milliseconds away.
   */
  sendCommand(command: EngineCommand): boolean {
    const commands = this.#commands;
    const timestamps = this.#timestamps;
    const shorts = this.#shorts;
    const floats = this.#floats;
    if (commands === null || timestamps === null || shorts === null || floats === null) {
      return false;
    }

    const offset = commands.claim();
    if (offset === NO_SLOT) {
      return false;
    }

    timestamps[offset >>> 3] = this.#context.currentTime;
    shorts[(offset + COMMAND_RECORD.OPCODE) >>> 1] = command.opcode;
    shorts[(offset + COMMAND_RECORD.TARGET) >>> 1] = command.target;
    floats[(offset + COMMAND_RECORD.VALUE) >>> 2] = command.value;

    commands.commit();
    return true;
  }

  /**
   * Sets the playback rate — frames of track per frame of output. Continuous, so it rides the
   * command ring rather than postMessage: Architecture §2's split.
   *
   * A convenience over `sendCommand` rather than a second path to the ring, and the distinction
   * matters: `/bench` and `#rebuild`'s re-arm both want a rate without going near the control plane,
   * while a finger on the pitch fader goes through the mapping table. Both end up in the same four
   * numbers, which is what makes the bench's latency figure comparable to a real session's.
   */
  setRate(rate: number): boolean {
    this.#rate = rate;
    return this.sendCommand({
      opcode: DECK_OPCODE.SET_RATE,
      target: 0,
      value: Math.min(DECK_MAX_RATE, Math.max(-DECK_MAX_RATE, rate)),
    });
  }

  /**
   * FR-10's normalisation gain (P3.2). Structural, so postMessage — see `DeckTrackGainMessage`.
   *
   * Remembered as well as sent, for the same reason the rate is: `#rebuild` hands the new processor
   * a fresh `DECK_DEFAULT_TRACK_GAIN`, and without re-arming, a node swap would quietly return a
   * normalised track to its raw level while every readout still said otherwise.
   */
  setTrackGain(gain: number): void {
    this.#trackGain = gain;
    const message: DeckTrackGainMessage = { type: 'set-track-gain', gain };
    this.#node?.port.postMessage(message);
  }

  /**
   * Hands the audio thread this track's beatgrid (P3.9). Null clears it.
   *
   * Structural, so postMessage — a grid changes when a track loads or FR-13 corrects it, not at
   * input rate. The processor keeps it in the worklet-scope registry the other deck reads from, so
   * this is also what makes *the other* deck able to sync to this one.
   *
   * Remembered for the same reason the rate and the track gain are: `#rebuild` hands the new
   * processor nothing, and a node swap that silently dropped the grid would leave sync aligning to
   * a beat the deck no longer knows about.
   */
  setBeatGrid(grid: SyncGrid | null): void {
    this.#beatGrid = grid;
    const message: DeckBeatGridMessage = { type: 'set-beatgrid', grid };
    this.#node?.port.postMessage(message);
  }

  get beatGrid(): SyncGrid | null {
    return this.#beatGrid;
  }

  /**
   * Asks the audio thread to align this deck's beat phase to the other deck's (P3.9) — FR-07.
   *
   * **Carries no offset, deliberately.** P3.9's rule: "a phase correction computed on the main
   * thread arrives late by definition". Everything this side knows about where the playheads are is
   * already stale by the time the message lands, and by a different amount on each deck if their
   * rates differ. The processor computes it from live positions when it acts.
   *
   * **Match tempo first.** This aligns phase and nothing else; a phase corrected between decks whose
   * effective tempos differ walks straight back out — at 128 against 130 BPM, a whole beat in about
   * half a minute. `useTrackLoader.sync` does both, in that order.
   */
  syncPhase(): void {
    const message: DeckSyncPhaseMessage = { type: 'sync-phase' };
    this.#node?.port.postMessage(message);
  }

  /**
   * The live phase error against the other deck, in seconds, as the **audio thread** measured it.
   * NaN when there is nothing to compare against.
   *
   * This is the number AC-2's ±10 ms is judged on, and it is read rather than computed here on
   * purpose: the main thread could pair two playheads out of one signal-ring frame — P3.7 made them
   * coherent precisely so it could — but that frame is already a block old and the grids would be
   * this side's copies. This is what the thread doing the aligning saw.
   */
  readSyncPhaseError(): number {
    return this.#status?.[SYNC_ERROR_F64_INDEX] ?? Number.NaN;
  }

  /**
   * FR-03's pads (P3.10). `set` means "wherever the playhead is", which only the audio thread knows.
   *
   * The whole set is re-read from the status buffer rather than mirrored here — see `readHotCues`.
   */
  hotCue(action: 'set' | 'clear' | 'jump', slot: number): void {
    const message: DeckHotCueMessage = { type: 'hot-cue', action, slot };
    this.#node?.port.postMessage(message);
  }

  /**
   * Puts a stored set of cues back (P3.10) — the cache's half of FR-03's "persisted".
   *
   * Remembered so `#rebuild` can re-arm them, exactly like the rate, the gain and the grid: a node
   * swap that dropped a user's cue points would lose data they set by hand, which is the one kind of
   * loss this deck must never cause.
   */
  restoreHotCues(frames: readonly number[]): void {
    this.#hotCues = [...frames];
    const message: DeckRestoreHotCuesMessage = { type: 'restore-hot-cues', frames };
    this.#node?.port.postMessage(message);
  }

  /**
   * Every cue slot, in frames, `NaN` for empty — as the **audio thread** has them.
   *
   * Copied into a caller-owned array rather than returning a view, because the view would be live
   * over a buffer the audio thread is writing and a caller holding it across a `#rebuild` would be
   * reading a deck that no longer exists.
   */
  readHotCues(destination: Float64Array): Float64Array {
    const status = this.#status;
    for (let slot = 0; slot < destination.length; slot += 1) {
      destination[slot] = status?.[HOT_CUES_F64_INDEX + slot] ?? Number.NaN;
    }
    return destination;
  }

  /**
   * FR-04's loop machine (P3.10). Boundaries are computed on the audio thread, so this says what to
   * do and never where.
   *
   * `quantization` is this side's judgement because it is this side that knows the tempo's
   * confidence — see `loopQuantization`. The processor is told whether to snap, never asked.
   */
  loop(
    action: 'auto' | 'in' | 'out' | 'exit' | 'reloop',
    beats?: number,
    quantization?: LoopQuantization,
  ): void {
    const message: DeckLoopMessage = { type: 'loop', action, beats, quantization };
    this.#node?.port.postMessage(message);
  }

  /** The loop as the audio thread has it. Read, never mirrored — the boundaries are its state. */
  readLoop(): {
    readonly active: boolean;
    readonly startFrames: number;
    readonly endFrames: number;
  } {
    const ints = this.#statusInts;
    return {
      active: ints !== null && Atomics.load(ints, LOOP_ACTIVE_I32_INDEX) === 1,
      startFrames: this.#status?.[LOOP_START_F64_INDEX] ?? 0,
      endFrames: this.#status?.[LOOP_END_F64_INDEX] ?? 0,
    };
  }

  /** What the deck is currently applying. `DECK_DEFAULT_TRACK_GAIN` means "as recorded". */
  get trackGain(): number {
    return this.#trackGain;
  }

  /** Current playhead, in fractional frames — see platform/types/deck.ts's DECK_STATUS for why
   * this is a plain read rather than an atomic one. */
  readPlayheadFrames(): number {
    return this.#status?.[POSITION_F64_INDEX] ?? 0;
  }

  get positionSeconds(): number {
    return this.readPlayheadFrames() / this.#context.sampleRate;
  }

  get durationSeconds(): number {
    const layout = this.#layout;
    return layout === null ? 0 : layout.frameCount / this.#context.sampleRate;
  }

  get format(): TrackMemoryFormat {
    return TRACK_STORAGE_FORMAT;
  }

  get trackMemoryBytes(): number {
    return this.#layout?.totalBytes ?? 0;
  }

  /**
   * The decoded PCM, as shared memory (P2.5).
   *
   * Exposed for the analysis worker, and read-only by convention rather than by type — a
   * `SharedArrayBuffer` has no read-only form. The convention holds because the only other reader
   * is the audio thread, and both treat the buffer as written-once-then-immutable, which is what
   * `track-memory.ts` is built around.
   */
  get trackMemory(): SharedArrayBuffer | null {
    return this.#trackMemory;
  }

  /** How to read `trackMemory`. Passed to the analysis worker so it derives the same layout from
   * the same function rather than re-deriving byte offsets of its own. */
  get trackSpec(): TrackMemorySpec | null {
    const layout = this.#layout;
    return layout === null
      ? null
      : {
          channelCount: layout.channelCount,
          frameCount: layout.frameCount,
          format: layout.format,
        };
  }

  get sampleRateHz(): number {
    return this.#context.sampleRate;
  }

  /** The AudioContext clock, for stamping `ControlEvent`s (P2.8). Architecture §4.5 wants control
   * events orderable against audio, and `performance.now()` is a different clock — P0.6's probe
   * measures nothing if the two are mixed. */
  get audioTime(): number {
    return this.#context.currentTime;
  }

  /** The signal ring, for anything that wants to read this deck's own signals — the waveform's
   * playhead (P2.6) and, at M4+, the scenes. */
  get signalBuffer(): SharedArrayBuffer {
    return this.#signals;
  }

  /**
   * This deck's loudest RMS since the last call, or null if the audio thread published nothing.
   *
   * Peak-across-the-interval, not the newest frame: ~6 frames arrive per 60 Hz redraw, so taking
   * only the last would throw away five out of six and miss exactly the transients a meter exists
   * to show (P0.7's reasoning, now applied to a deck rather than the master).
   */
  readDeckPeak(): number | null {
    return this.#signalReader.readSourcePeak(this.#source);
  }

  /**
   * Where the audio thread publishes the playhead (P2.6).
   *
   * Shared with the waveform display so it reads the transport's own position rather than
   * integrating a frame delta of its own. A **new buffer is created on every `#rebuild`**, so a
   * consumer that cached one is watching a deck that no longer exists — which is why the waveform
   * is re-pointed at it on every load rather than once at startup.
   */
  get statusBuffer(): SharedArrayBuffer | null {
    return this.#statusBuffer;
  }

  /**
   * Architecture §12's underrun counter, reused exactly as P0.12's bench uses it — see
   * clock-drift.ts for why a block-skip counter cannot see this and a clock-drift one can.
   *
   * ## ⚠️ Refuses to sample a context that is not running, and that guard is the whole method
   *
   * `currentTime` **does not advance while an AudioContext is suspended**, and every context starts
   * suspended: the autoplay policy holds it there until a user gesture. Wall time advances anyway,
   * so drift accrues at a clean 1000 ms per second — one second of suspension looks exactly like
   * one second of total starvation, because arithmetically it is the same reading.
   *
   * Measured at P4.16, the moment the app started polling this: an end-to-end test reported
   * **895 ms of drift** across a ten-second page life, on a machine doing nothing wrong. That is
   * the page sitting at its autoplay gate, and a detector built to tell a healthy machine from a
   * starving one had no way to tell either from a silent one.
   *
   * `/bench` never saw it because `resetProbe()` runs after play starts, which threw the suspended
   * stretch away by luck rather than by design. The guard belongs here, where the context is, so
   * that being right is not something each caller has to remember.
   *
   * **Reset rather than merely skipped**, because a suspension can also happen *mid-session* — a
   * context suspended and resumed would otherwise inject the whole gap as one enormous step, and
   * drift measured across a period when the engine was not running is not a measurement of
   * anything. The counter starts again from the moment audio does.
   */
  sampleClockDrift(): void {
    const audioMs = this.#context.currentTime * 1000;

    if (this.#context.state !== 'running') {
      this.#drift.reset();
      this.#clockStarted = false;
      this.#lastAudioMs = null;
      return;
    }

    // ⚠️ **`state === 'running'` is not the same as "the clock is moving", and the gap is ~250 ms.**
    // Chrome flips the state as soon as it accepts the resume, then opens the output device; until
    // that device delivers its first callback `currentTime` stands still while wall time does not.
    // Measured at P4.16 across eight end-to-end runs: a tight 237–290 ms of drift on every page,
    // far too consistent to be starvation and exactly the shape of a fixed startup cost.
    //
    // **A one-way gate, not a filter on stalls**, and the difference is the whole design. Once the
    // clock has been seen to move, a later failure to move is precisely the underrun this class
    // exists to catch — resetting then would erase the only event worth reporting. So this asks
    // "has audio ever started?", answers it once, and never asks again until the context stops.
    if (!this.#clockStarted) {
      const previous = this.#lastAudioMs;
      this.#lastAudioMs = audioMs;
      if (previous === null || audioMs <= previous) {
        this.#drift.reset();
        return;
      }
      this.#clockStarted = true;
    }

    this.#drift.sample({ wallMs: performance.now(), audioMs });
  }

  get underruns(): UnderrunReport {
    return this.#drift.report;
  }

  /**
   * Signal frames the audio thread overwrote before this reader reached them.
   *
   * Expected to climb, and not a fault: the deck publishes once per 128-sample block (~375 Hz) and
   * a screen redraws at 60. Overwrite-oldest means the meter reads the newest level and the rest
   * are discarded by design (P0.3). Surfaced precisely because a number that looks alarming and is
   * not teaches the shape of the policy.
   */
  get signalsDropped(): number {
    return this.#signalReader.dropped;
  }

  /**
   * The latency figures this device actually spends NFR-P1's 35 ms budget against (P2.12).
   *
   * Inherited from `AudioEngine.info` when it retired. Every one of these is a property of the
   * browser and the sound card rather than of this code, which is the whole reason the bench
   * records them: four over-budget latency readings in this repo's history were ~40 ms of
   * environment wrapped around ~8 ms of engine, and a report that could not separate the two would
   * blame the code for the hardware.
   */
  get info(): DeckLatencyInfo {
    const outputLatency = (this.#context as MaybeOutputLatency).outputLatency ?? null;
    const baseLatency = this.#context.baseLatency;

    return {
      sampleRateHz: this.#context.sampleRate,
      baseLatencySeconds: baseLatency,
      outputLatencySeconds: outputLatency,
      totalLatencySeconds: outputLatency === null ? null : baseLatency + outputLatency,
    };
  }

  /**
   * Architecture §4.5's probe, read back. Every figure is command→audible, not internal.
   *
   * The worklet measures command→applied; the speaker is another `outputLatency` beyond that, so
   * that is added here. What the user experiences is the sum, and NFR-P1's 35 ms is a claim about
   * the sum rather than about the part that flatters us.
   *
   * ⚠️ **`outputLatencyMs` is a parameter since P4.14, and a caller building one report must pass
   * the same value here and to `readLatencyDistribution`.** It defaults to a live read, which is
   * what every single-figure caller wants — but `outputLatency` is *not* constant for the life of
   * the context, and a report that reads it separately for its max and its percentiles produces two
   * figures that cannot both be true. The 11 September bench did exactly that, with a
   * garbage-collection pause between the reads, and reported a max 5 ms below its own p50.
   */
  readProbe(
    outputLatencyMs = (this.info.outputLatencySeconds ?? 0) * 1000,
  ): LatencyProbe {
    const appliedCount = Atomics.load(this.#probeInts, PROBE_COUNT_I32);

    const toMs = (micros: number): number | null =>
      appliedCount === 0 ? null : micros / 1000 + outputLatencyMs;

    return {
      lastMs: toMs(Atomics.load(this.#probeInts, PROBE_LAST_I32)),
      maxMs: toMs(Atomics.load(this.#probeInts, PROBE_MAX_I32)),
      appliedCount,
      rejectedCount: this.#commands?.dropped ?? 0,
    };
  }

  /**
   * The probe's distribution, in ms, **since the last time this was called** (P2.10, moved P2.12).
   *
   * Reads and then clears, so each call is a window rather than a running total — the shape
   * telemetry wants, since NFR-P1's p90 is a claim about a stretch of use and a cumulative
   * distribution sent repeatedly would weight the early minutes more heavily every time.
   *
   * Returns null when the window is under `minimumCount`, leaving the histogram **untouched** so a
   * thin stretch rolls forward rather than being thrown away. Null rather than an empty
   * distribution: "nobody touched a control" and "every control was instant" are different facts.
   */
  takeLatencyWindow(minimumCount = 1): DistributionMs | null {
    return takeWindow(
      LATENCY_HISTOGRAM,
      this.#probeInts,
      PROBE_HISTOGRAM_I32,
      minimumCount,
      (this.info.outputLatencySeconds ?? 0) * 1000,
    );
  }

  /**
   * The distribution without consuming it — for a report that must not disturb other readers.
   *
   * See `readProbe` on why `outputLatencyMs` is a parameter: a report quoting a max and a p90 must
   * offset both by the *same* reading, or the two describe different devices.
   */
  readLatencyDistribution(
    outputLatencyMs = (this.info.outputLatencySeconds ?? 0) * 1000,
  ): DistributionMs {
    return readDistribution(
      LATENCY_HISTOGRAM,
      this.#probeInts,
      PROBE_HISTOGRAM_I32,
      outputLatencyMs,
    );
  }

  /**
   * Make the audio thread miss its deadline, on purpose (P0.12's bench, inherited at P2.12).
   *
   * The only caller is the bench, and its only purpose is to prove the underrun counter can move
   * off zero. "Zero underruns" and "the counter was never wired up" are the same reading, and this
   * repo has shipped that confusion six times; an underrun is a real-time event, so causing one is
   * the only way to tell them apart.
   */
  setSyntheticLoad(busyMs: number): void {
    const message: DeckMessage = { type: 'set-synthetic-load', busyMs };
    this.#node?.port.postMessage(message);
  }

  /**
   * Iterations the synthetic-load spin has burned (P2.12).
   *
   * Exists so "the load knob did nothing" is a *reading* rather than a silence. It read zero for
   * six prompts in every production build, because the empty spin loop was dead code a minifier
   * removed — see `DECK_STATUS.SYNTHETIC_SPINS`. Zero here while `setSyntheticLoad` was asked for a
   * non-zero burn is now a failed budget in the bench report instead of a number nobody checks.
   */
  get syntheticSpins(): number {
    const ints = this.#statusInts;
    return ints === null ? 0 : Atomics.load(ints, SPIN_I32_INDEX);
  }

  /**
   * Zeroes the probe, the drift monitor and the spin counter, so a fresh bench run is measured on
   * its own.
   *
   * ⚠️ **The spin counter was missing from this list until 13 September**, and the first complete
   * soak printed the consequence: `syntheticLoadMs: 0` beside `syntheticSpins: 1,809,578,161`. Both
   * numbers were true and they were about different runs — the spins were left over from a
   * 60-second overload check minutes earlier, because the bench reuses its decks when the track
   * length has not changed.
   *
   * No budget was misled (`harness-synthetic-load` reads null when no load was asked for, and never
   * looks at the count). A human reading the JSON was, which is the same failure one layer up: a
   * number printed beside a window it does not belong to.
   */
  resetProbe(): void {
    Atomics.store(this.#probeInts, PROBE_LAST_I32, 0);
    Atomics.store(this.#probeInts, PROBE_MAX_I32, 0);
    Atomics.store(this.#probeInts, PROBE_COUNT_I32, 0);
    clearHistogram(LATENCY_HISTOGRAM, this.#probeInts, PROBE_HISTOGRAM_I32);
    // Written by the audio thread, cleared here — the same arrangement as the probe above, and safe
    // for the same reason: the worklet only ever adds to it, so a store of 0 cannot lose a count
    // that mattered to a run which has not started yet.
    const status = this.#statusInts;
    if (status !== null) {
      Atomics.store(status, SPIN_I32_INDEX, 0);
    }
    this.#drift.reset();
  }

  /**
   * Tears down this deck's node. **Does not close the context** — since P3.7 the context belongs to
   * the graph and the other deck is still using it. `AudioGraph.close()` is what ends the session.
   *
   * Still async, and not for show: the signature is what App and `/bench` already `void`, and
   * whatever a future teardown has to await (an OPFS flush at P3.8) belongs here rather than in a
   * second method nobody remembers to call.
   */
  async dispose(): Promise<void> {
    this.#node?.port.close();
    this.#node?.disconnect();
    this.#node = null;
    return Promise.resolve();
  }
}
